import { createSelector } from '@reduxjs/toolkit';
import { useEffect, useMemo, useRef } from 'preact/hooks';

import { AppState, useSelector } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import { selectHasExplorerFeature } from '@/redux/selectors/users';

const selectIsAllHabitatsOffline = createSelector(
  (state: AppState) => state.allHabitats,
  (habitats) => habitats.length > 0 && habitats.every(({ isStreamOn }) => !isStreamOn),
);

interface UseHabitatForStreamOptions {
  keepPreferredHabitatId: boolean;
  restrictToFreemiumHabitats: boolean;
}

export const useHabitatForStream = ({ keepPreferredHabitatId, restrictToFreemiumHabitats }: UseHabitatForStreamOptions) => {
  const preferredHabitatIdRef = useRef<string>();
  const selectHabitatForStream = useMemo(
    () =>
      createSelector(
        createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
        selectHasExplorerFeature,
        (_: AppState, habitatId: string | undefined) => habitatId,
        (sortedHabitats, hasExplorerFeature, habitatId) => {
          const shouldRestrict = restrictToFreemiumHabitats && !hasExplorerFeature;
          const habitats = shouldRestrict ? sortedHabitats.filter(({ isFreemium }) => isFreemium) : sortedHabitats;
          const habitat = habitatId ? habitats.find((habitat) => habitat._id === habitatId) : undefined;
          if (habitat && habitat.isStreamOn) {
            return habitat;
          }
          return habitats.find(({ isStreamOn }) => isStreamOn);
        },
      ),
    [restrictToFreemiumHabitats],
  );

  const habitat = useSelector((state) =>
    selectHabitatForStream(state, keepPreferredHabitatId ? preferredHabitatIdRef.current : undefined),
  );

  useEffect(() => {
    preferredHabitatIdRef.current = habitat?._id;
  }, [habitat?._id]);

  return habitat;
};

export const useIsAllHabitatsOffline = () => useSelector(selectIsAllHabitatsOffline);
